import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useState, useRef, useId, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Carousel.css";

const Slide = ({ slide, index, current, handleSlideClick }) => {
    const slideRef = useRef(null);

    const xRef = useRef(0);
    const yRef = useRef(0);
    const frameRef = useRef();

    useEffect(() => {
        const animate = () => {
            if (!slideRef.current) return;

            const x = xRef.current;
            const y = yRef.current;

            slideRef.current.style.setProperty("--x", `${x}px`);
            slideRef.current.style.setProperty("--y", `${y}px`);

            frameRef.current = requestAnimationFrame(animate);
        };

        frameRef.current = requestAnimationFrame(animate);

        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, []);

    const handleMouseMove = (event) => {
        const el = slideRef.current;
        if (!el) return;

        const r = el.getBoundingClientRect();
        xRef.current = event.clientX - (r.left + Math.floor(r.width / 2));
        yRef.current = event.clientY - (r.top + Math.floor(r.height / 2));
    };

    const handleMouseLeave = () => {
        xRef.current = 0;
        yRef.current = 0;
    };

    const imageLoaded = (event) => {
        event.currentTarget.style.opacity = "1";
    };

    const { src, button, title, link, objectPosition } = slide;
    const isCurrent = current === index;
    const navigate = useNavigate();

    const handleBuyClick = (event) => {
        event.stopPropagation();
        if (link) navigate(link);
    };

    return (
        <div className="carousel-slide-perspective">
            <li
                ref={slideRef}
                className="carousel-slide"
                onClick={() => handleSlideClick(index)}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{
                    transform: isCurrent
                        ? "scale(1) rotateX(0deg)"
                        : "scale(0.98) rotateX(8deg)",
                }}
            >
                <div
                    className="carousel-slide-image-wrapper"
                    style={{
                        transform: isCurrent
                            ? "translate3d(calc(var(--x) / 30), calc(var(--y) / 30), 0)"
                            : "none",
                    }}
                >
                    <img
                        className="carousel-slide-image"
                        style={{
                            opacity: isCurrent ? 1 : 0.5,
                            objectPosition: objectPosition || "center",
                        }}
                        alt={title}
                        src={src}
                        onLoad={imageLoaded}
                        loading="eager"
                        decoding="sync"
                    />
                    {isCurrent && <div className="carousel-slide-overlay" />}
                </div>

                <article
                    className={`carousel-slide-content ${
                        isCurrent ? "carousel-slide-content-visible" : "carousel-slide-content-hidden"
                    }`}
                >
                    <h2 className="carousel-slide-title">{title}</h2>
                    <div className="carousel-slide-button-row">
                        <button className="carousel-slide-button" onClick={handleBuyClick}>{button}</button>
                    </div>
                </article>
            </li>
        </div>
    );
};

const CarouselControl = ({ type, title, handleClick }) => {
    return (
        <button
            className={`carousel-control ${type === "previous" ? "carousel-control-previous" : ""}`}
            title={title}
            onClick={handleClick}
        >
            <IconArrowNarrowRight className="carousel-control-icon" />
        </button>
    );
};

export function Carousel({ slides, heading }) {
    const [current, setCurrent] = useState(0);

    const handlePreviousClick = () => {
        const previous = current - 1;
        setCurrent(previous < 0 ? slides.length - 1 : previous);
    };

    const handleNextClick = () => {
        const next = current + 1;
        setCurrent(next === slides.length ? 0 : next);
    };

    const handleSlideClick = (index) => {
        if (current !== index) {
            setCurrent(index);
        }
    };

    const id = useId();
    const headingId = `carousel-heading-${id}`;

    return (
        <section className="carousel-section">
            {heading && (
                <div className="carousel-heading-wrap">
                    <span className="carousel-eyebrow" aria-hidden="true" />
                    <h2 id={headingId} className="carousel-heading">
                        {heading}
                    </h2>
                </div>
            )}

            <div className="carousel" aria-labelledby={heading ? headingId : undefined}>
                <ul
                    className="carousel-track"
                    style={{
                        transform: `translateX(-${current * (100 / slides.length)}%)`,
                    }}
                >
                    {slides.map((slide, index) => (
                        <Slide
                            key={index}
                            slide={slide}
                            index={index}
                            current={current}
                            handleSlideClick={handleSlideClick}
                        />
                    ))}
                </ul>

                <div className="carousel-controls">
                    <CarouselControl
                        type="previous"
                        title="Ir a la diapositiva anterior"
                        handleClick={handlePreviousClick}
                    />

                    <CarouselControl
                        type="next"
                        title="Ir a la siguiente diapositiva"
                        handleClick={handleNextClick}
                    />
                </div>
            </div>
        </section>
    );
}
